import { QUERY_KEYS } from "@/common/query-keys";
import { useSettings } from "@/context/settings-context";
import { countHardwareByModel } from "@/lib/snipeit/hardware";
import { fetchModelById } from "@/lib/snipeit/lookups";
import { useQuery } from "@tanstack/react-query";

export interface DashboardModelCount {
  id: number;
  name: string;
  image?: string | null;
  count: number;
}

export function useDashboardCounts() {
  const { dashboardModels, locationId } = useSettings();

  const modelIds = dashboardModels.map((m) => m.id);

  const { data: counts, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.dashboardModelCount, modelIds, locationId ?? null],
    queryFn: async () => {
      const results = await Promise.allSettled(
        modelIds.map(async (id) => {
          const count = await countHardwareByModel(id, locationId ?? undefined);
          const modelData = await fetchModelById(id);
          return { id, count, image: modelData?.image };
        }),
      );
      return results.map((result, index) => {
        if (result.status === "fulfilled") return result.value;
        console.warn(`Failed to fetch counts for model ${modelIds[index]}`, result.reason);
        return { id: modelIds[index], count: 0, image: undefined }; // Fallback preserving the original model ID
      });
    },
    refetchInterval: 30_000,
    enabled: modelIds.length > 0,
  });

  const items: DashboardModelCount[] = dashboardModels.map((model) => {
    const result = counts?.find((c) => c.id === model.id);
    return {
      id: model.id,
      name: model.name,
      image: result?.image !== undefined ? result.image : model.image,
      count: result?.count ?? 0,
    };
  });

  return { items, isLoading };
}
